import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { modeConstants } from "../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { companyActions } from "../../../redux/actions";
import PhoneInput from "react-phone-input-2";

const schema = z.object({
  companyName: z.string().min(2, "The field is required"),
  contactNumber: z
    .string()
    .regex(/^\d{11,}$/, "Contact number must be at least 11 digits"),
    licenseStartDate: z.string().min(1, "Start date is required"),
    licenseEndDate: z.string().min(1, "End date is required"),
});

const EMPTY_VALUES = {
  companyName: "",
  contactNumber: "",
  licenseStartDate: "",
  licenseEndDate: ""
};

export default function PlatformModal({ showModal, setShowModal, mode }) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_VALUES,
  });

  const dispatch = useDispatch();
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;
  const [countryCode, setCountryCode] = useState("pk");
  const initialData = useSelector((s) => s.companyReducer.formData);

  // 🔁 Prefill from initialData when editing/viewing; clear when adding
  useEffect(() => {
    if (isAdd) {
      reset(EMPTY_VALUES);
      return;
    }
    if (initialData) {
      reset({
        companyName: initialData.companyName ?? "",
        licenseStartDate: initialData.licenseStartDate ?? "",
        licenseEndDate: initialData.licenseEndDate ?? "",
        contactNumber: String(initialData.contactNumber) || "",
      });
    }
  }, [isAdd, initialData, reset]);

  const handleHide = () => {
    reset(EMPTY_VALUES);
    setShowModal(false);
  };

  const onSubmit = async (data) => {
    // --- Normalize contact number: remove non-digits and prefix 92 ---
    const digitsOnly = (data.contactNumber || "").replace(/\D+/g, "");
    const contactNumber = digitsOnly;

    const fd = {
      companyName: data.companyName?.trim(),
      contactNumber: contactNumber,
      id: String(initialData.id),
      licenseEndDate: data.licenseEndDate,
      licenseStartDate: data.licenseStartDate
    };
    try {
      if (isEdit) {
        dispatch(companyActions.updatePlatformCompany(fd));
      }
      handleHide();
    } catch (err) {
      console.error("Network error while uploading company:", err);
      // TODO: show UI error / toast
    }
  };

  const title = isAdd
    ? "Create Company"
    : isEdit
    ? "Edit Company"
    : "View Company";

  return (
    <Modal show={showModal} onHide={handleHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="form-floating">
          <fieldset disabled={isView}>
            <div className="row">
              <div className="col-lg-12">
                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        id="companyName"
                        className="form-control"
                        placeholder="Company Name"
                        {...register("companyName")}
                      />
                      <label for="companyName">Company Name</label>
                    </div>
                    {errors.companyName && (
                      <small className="text-danger">
                        {errors.companyName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <Controller
                        name="contactNumber"
                        control={control}
                        rules={{
                          required: "The field is required",
                          validate: (val) =>
                            (val && val.length >= 8) || "Invalid number",
                        }}
                        render={({ field: { value, onChange } }) => (
                          <PhoneInput
                            country={countryCode}
                            value={value.toString()}
                            onChange={(val, country) => {
                              onChange(val);
                              if (country?.countryCode)
                                setCountryCode(country.countryCode);
                            }}
                            inputProps={{
                              name: "contactNumber",
                              required: true,
                              autoFocus: false,
                            }}
                            placeholder="Enter phone number"
                            enableSearch
                            disableSearchIcon={false}
                            inputStyle={{ width: "100%" }}
                            buttonStyle={{
                              borderTopLeftRadius: 6,
                              borderBottomLeftRadius: 6,
                            }}
                            containerStyle={{ width: "100%" }}
                            disabled={isView}
                          />
                        )}
                      />
                    </div>
                    {errors.contactNumber && (
                      <small className="text-danger">
                        {errors.contactNumber.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        type="date"
                        id="licenseStartDate"
                        className="form-control"
                        placeholder="Company Name"
                        {...register("licenseStartDate")}
                      />
                      <label for="licenseStartDate">License Start Date</label>
                    </div>
                    {errors.licenseStartDate && (
                      <small className="text-danger">
                        {errors.licenseStartDate.message}
                      </small>
                    )}
                  </div>
                  <div className="col-12 col-lg-6">
                    <div class="form-floating">
                      <input
                        type="date"
                        id="licenseEndDate"
                        className="form-control"
                        placeholder="Company Name"
                        {...register("licenseEndDate")}
                      />
                      <label for="licenseEndDate">License Expiry Date</label>
                    </div>
                    {errors.licenseEndDate && (
                      <small className="text-danger">
                        {errors.licenseEndDate.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <div className="d-flex justify-content-end gap-2 footer-modal">
            <Button variant="outline-secondary btn-footer" onClick={handleHide}>
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                {isEdit ? "Save Changes" : "Create"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
